import uuid from 'node-uuid'
import 'UI/Widget/Base'
import 'System/Promises'
import '../Geog'
import '../Map/Control/Base'
import '../Geometry'
import '../Map/Layer/Base'
import '../Data/Projections'
import '../Data/Layers'
import '../Data/LayerFeatures'
import '../Map/Layer/Tile'
import '../Map/Layer/WMS'
import '../Map/Layer/WFS'
import '../Map/Layer/Sputnik'
import '../Map/Layer/OSM'
import '../Map/Layer/GlonassTile'
import '../Map/Layer/GeoanalitikaAgro'
import '../Map/Layer/Google'
import '../Map/Layer/Yandex'
import '../Map/Layer/GlonassVector'
import '../Map/Control/Layers'
import '../Map/Control/Zoom'
import '../Map/Control/FullScreen'
import '../Map/Control/LocateMe'
import '../Map/Control/FollowMe'
import '../Map/Control/OL3/Draw'

/** @namespace GIS.UI */
NS("GIS.UI");

/**
 * @class
 * @extends UI.Widget.Base
 */
GIS.UI.Map = Class(UI.Widget.Base, System.Promises, /** @lends GIS.UI.Map.prototype */ {
    // options
    template: "GIS/Map/Map",
    "class": null,
    /** @type {Element} */
    keyboardEventTarget: undefined,
    initLayers: {},
    initControls: {},
    projection: MAP_PROJ,
    displayProjection: DISPLAY_PROJ,
    geoloc: true,
    geolocMarker: false,
    center: [0, 0],
    minZoom: 0,
    maxZoom: 22,
    zoom: 1,
    renderer: 'canvas',
    popupClass: "",
    layersSourceClass: "GIS.Data.Layers",
    featuresSourceClass: "GIS.Data.LayerFeatures",
    projectionsSourceClass: "GIS.Data.Projections",
    /**
     * @type {GIS.Map.Base}
     * @protected
     */
    map: null,
    /**
     * @type {Object.<string, GIS.Map.Control.Base>}
     * @protected
     */
    controls: {},
    $map: null,
    $mapControls: null,
    layersSource: null,
    projectionsSource: null,
    /**
     * @inheritDoc
     */
    initialize(parent, options = {})
    {
        this.Parent(parent, _.merge({}, _.get(application.configuration, 'ui.mapOptions'), options));

        this.layersSource = Util.create(this.layersSourceClass);

        if (this.projectionsSourceClass) {
            this.projectionsSource = Util.create(this.projectionsSourceClass);
        }
    },
    /**
     * @returns {GIS.UI.Map}
     */
    destroy() {
        this.cancelAllPromises();

        Util.each(this.controls, control => control.destroy());

        if (this.map) {
            this.map.destroy() && (this.map = null);
        }

        this.$map = null;
        this.$mapControls = null;
        this.initLayers = {};
        this.initControls = {};
        this.keyboardEventTarget = null;

        this.layersSource = this.projectionsSource = null;

        return this.ParentCall();
    },
    /**
     * @param options
     * @returns {GIS.UI.Map}
     */
    render(options) {
        this.Parent();

        this.$map = this.$name("map-container");
        this.$mapControls = this.$name('map-controls');

        Promise.resolve(this.createMap(options))
            .then(() => {
                this.align();

                const position = this.map.currentPosition;

                if (_.isArray(position)) {
                    this.setCenter(this.map.toGeoJSON(position, GIS.Geometry.Point));
                }

                this.createLayers();
                this.createControls();

                this.emit("ready");
            })
            .catch(err => console.warn(err.stack));

        return this;
    },
    /**
     * @param {Object} options
     * @returns {Promise<GIS.Map.Base>}
     */
    async createMap(options) {
        if (this.projectionsSource) {
            await this.addPromise("createMap", defineServerProjections(this.class, this.projectionsSource));
        }

        this.map = new this["class"](this.$map[0], this.mapOptions(options));

        this.map.on("click", this.onMapClick, this);

        return this.map;
    },
    /**
     * @param {Object} options
     * @returns {Object}
     */
    mapOptions(options = {})
    {
        const optKeys = ["geoloc", "geolocMarker", "center", "zoom", "maxZoom", "minZoom", "projection",
            "keyboardEventTarget", 'interactionOptions', 'renderer'];

        return {
            ...Util.pickOptions(this, optKeys),
            ...Util.pickOptions(options, optKeys),
            keyboardEventTarget: this.element
        };
    },
    /**
     * @returns {Array<GIS.Map.Layer.Base>}
     */
    createLayers() {
        const initLayers = _.pickBy(this.initLayers, _.identity);

        if (_.isEmpty(initLayers)) {
            return [];
        }

        return _.map(initLayers, (layerOptions, name) => this.addLayer(name, layerOptions.class, layerOptions));
    },
    /**
     * @returns {Array<GIS.Map.Control.Base>}
     */
    createControls() {
        const initControls = _.pickBy(this.initControls, _.identity);

        if (_.isEmpty(initControls)) {
            return [];
        }

        return _.map(initControls, (controlOptions, name) => this.addControl(name, controlOptions.class, controlOptions));
    },
    /**
     * @returns {GIS.Map.Base}
     */
    getMap() {
        return this.map;
    },
    /**
     * @returns {GIS.UI.Map}
     */
    align() {
        this.ParentCall();

        this.map && this.map.align();

        return this;
    },
    /**
     * @returns {GIS.UI.Map}
     */
    refresh() {
        this.map && this.map.refresh();

        return this;
    },
    /**
     * @param name
     * @param layerClass
     * @param options
     * @returns {GIS.Map.Layer.Base}
     */
    addLayer(name, layerClass, options) {
        return this.map && this.map.addLayer(name, layerClass, options);
    },
    /**
     * @param name
     * @returns {GIS.UI.Map}
     */
    removeLayer(name) {
        this.map && this.map.removeLayer(name);

        return this;
    },
    /**
     * @param name
     * @returns {GIS.Map.Layer.Base|undefined}
     */
    getLayer(name) {
        return this.map && this.map.getLayer(name);
    },
    /**
     * @returns {Array.<GIS.Map.Layer.Base>}
     */
    getLayers() {
        return this.map && this.map.getLayers();
    },
    /**
     * @fires GIS.UI.Map#add.control
     * @param name
     * @param controlClass
     * @param options
     * @returns {GIS.Map.Control.Base}
     * @throws RuntimeError
     */
    addControl(name, controlClass, options = {}) {
        var control;

//        options.target = this.$mapControls;

        if (Util.isString(controlClass)) {
            control = Util.create(controlClass, [ this, options ]);
        } else if (Util.isFunction(controlClass)) {
            control = new controlClass(this, options);
        } else {
            control = controlClass;
        }

        if (!Class.Is(control, GIS.Map.Control.Base)) {
            throw new RuntimeError("wrong control argument");
        }

        this.controls[name] = control;

        control.on("destroy", () => {
            delete this.controls[name];

            this.emit("remove.control", { control });
        });

        this.emit("add.control", {
            control
        });

        return control;
    },
    /**
     * @param name
     * @returns {GIS.UI.Map}
     * @fires GIS.UI.Map#remove.control
     */
    removeControl(name) {
        if (this.controls[name]) {
            this.controls[name].destroy();
        }

        return this;
    },
    /**
     * @returns {Array.<GIS.Map.Control.Base>}
     */
    getControls() {
        return _.values(this.controls);
    },
    /**
     * @param {string} name
     * @returns {GIS.Map.Control.Base|undefined}
     */
    getControl(name) {
        return this.controls[name];
    },
    /**
     * @param {function} predicate
     * @param {*} [context]
     * @returns {GIS.Map.Control.Base|undefined}
     */
    findControl(predicate, context) {
        return _.find(this.getControls(), predicate, context || this);
    },
    /**
     * @param geog
     * @param zoom
     * @returns {GIS.UI.Map}
     */
    setCenter(geog, zoom) {
        this.map && this.map.setCenter(geog, zoom);

        return this;
    },
    /**
     * @returns {number[]|undefined}
     */
    getCenter() {
        return this.map && this.map.getCenter();
    },
    /**
     * @param zoom
     * @returns {GIS.UI.Map}
     */
    setZoom(zoom) {
        this.map && this.map.setZoom(zoom);

        return this;
    },
    /**
     * @returns {number|undefined}
     */
    getZoom() {
        return this.map && this.map.getZoom();
    },
    /**
     * @param args
     * @protected
     * @fires GIS.UI.Map#click
     */
    onMapClick(args) {
        this.emit("click", args);
    },
    /**
     * @returns {GIS.UI.Map}
     */
    zoomIn() {
        this.map && this.map.zoomIn();

        return this;
    },
    /**
     * @returns {GIS.UI.Map}
     */
    zoomOut() {
        this.map && this.map.zoomOut();

        return this;
    },
    /**
     * @param {number} delta
     * @returns {GIS.UI.Map}
     */
    zoomByDelta(delta) {
        this.map && this.map.zoomByDelta(delta);

        return this;
    },
    /**
     * @param {function(a : GIS.Map.Layer.Base, b : GIS.Map.Layer.Base)} iterator
     * @param {*|null} [context]
     * @returns {GIS.UI.Map}
     */
    sortLayers(iterator, context) {
        this.map && this.map.sortLayers(iterator, context);

        return this;
    },
    /**
     * @param layername
     * @returns {GIS.Map.Layer.Base}
     * todo check layer name too
     */
    findLayer(layername) {
        return this.map.getLayer(layername) || this.map.findLayer((layer) => layer.name === layername);
    },
    /**
     * @param {number} id - guid слоя
     * @param {boolean} [force=false]
     * @param {Object} [createOptions] Additional options
     * @returns {Promise.<GIS.Map.Layer.Base>}
     */
    async loadLayer(id, force, createOptions) {
        const layer = Class.Is(id, GIS.Map.Layer.Base) ? id : this.findLayer(id);

        if (layer && !force) {
            layer.updateOptions(createOptions)

            return layer;
        }

        const options = {};

        options.id = id;

        const { item } = await this.layersSource.getDocument(options);

        return this.createLayer(item, createOptions);
    },
    /**
     * @fires Spatial.UI.Map#layer.show
     * @param {string|Object} idOrLayerData - UUID или объект \GIS\Data\Layers
     * @param {boolean} [force=false] Удалить и пересоздать заново
     * @param {Object} [createOptions]
     * @returns {Promise.<GIS.Map.Layer.Base>}
     * @throws InvalidArgumentError
     */
    async showLayer(idOrLayerData, force, createOptions = {}) {
        let layer;

        // if passed ID or UUID of layer
        if (_.isString(idOrLayerData) || Util.isNumeric(idOrLayerData)) {
            layer = await this.addPromise(`showLayer-${createOptions.name ? createOptions.name : idOrLayerData}`, this.loadLayer(idOrLayerData, force, createOptions));
        } else if (_.isObject(idOrLayerData)) {
            layer = await this.addPromise(`showLayer-${createOptions.name ? createOptions.name : idOrLayerData.id}`, this.createLayer(idOrLayerData, createOptions));
        } else {
            throw new InvalidArgumentError("first argument isn't number or object");
        }

        this.addLayer(Util.coalesce(layer.name, uuid.v4()), layer);
        layer.show();
        /**
         * @event Spatial.UI.Map#layer.show
         * @type {{layer: GIS.Map.Layer.Base}}
         */
        this.emit("layer.show", { layer });

        return layer;
    },
    /**
     * @fires Spatial.UI.Map#layer.hide
     * @param {string} layerName - уникальное имя слоя
     * @param {boolean} [remove=false] Удалить слой после скрытия
     * @returns {Promise.<GIS.Map.Layer.Base>}
     * @throws RuntimeError
     */
    async hideLayer(layerName, remove) {
        const layer = this.findLayer(layerName);

        this.cancelPromise(`showLayer-${layerName}`);

        if (!layer) {
            throw new RuntimeError(`layer with id ${layerName} not found`);
        }

        layer.hide();
        /**
         * @event Spatial.UI.Map#layer.hide
         * @type {{layer: GIS.Map.Layer.Base}}
         */
        this.emit("layer.hide", { layer });

        if (remove) {
            layer.destroy();
        }

        return layer;
    },
    /**
     * Creates layer from \GIS\Data\Layers item
     * @param {Object} layerData
     * @param {Object} [additions]
     * @returns {Promise.<GIS.Map.Layer.Base>}
     */
    createLayer(layerData, additions) {
        _.merge(layerData, {
            ds: Util.create(this.featuresSourceClass),
            layerid: layerData.id,
            filter_: additions.filter
        });
        delete additions.filter;
        const layerOptions = this.layersSource.toLayerOptions(layerData);

        // merge additional options
        _.merge(layerOptions.options, additions);

        return Util.create(layerOptions.class, [ this.map, layerOptions.options ]);
    },
    /**
     * @param {number[]} geom
     * @param {number[]} [size]
     * @param {*} [options]
     * @returns {GIS.UI.Map}
     */
    fit(geom, size, options = {}) {
        this.map.fit(geom, size, options);

        return this;
    },
    /**
     * @returns {*|string}
     */
    getProjection()
    {
        return this.map.getProjection();
    },
    /**
     * @returns {number[] | undefined}
     */
    getSize()
    {
        return this.map.getSize();
    },
    /**
     * @param {number[]} coordinate
     * @param {string} source Source projection code
     * @param {string} destination Destination projection code
     * @returns {number[]}
     */
    transformCoordinateProjection(coordinate, source, destination)
    {
        return this.map.transformCoordinateProjection(coordinate, source, destination);
    },
    /**
     * @param {number[]} extent
     * @param {string} source
     * @param {string} destination
     * @returns {number[]}
     */
    transformExtentProjection(extent, source, destination)
    {
        return this.map.transformExtentProjection(extent, source, destination);
    },
    /**
     * @param {Object|string} geoJson
     * @param {string} [geometryProjection]
     * @param {string} [geoJSONProjection]
     * @returns {*}
     */
    geomFromGeoJSON (geoJson, geometryProjection = this.getProjection(), geoJSONProjection = this.displayProjection) {
        return this.map.geomFromGeoJSON(geoJson, geometryProjection, geoJSONProjection)
    },
    /**
     * @param {Function|Object|GIS.Map.Base} map
     * @param {Data.ServerSource} source
     * @returns {Promise<Array>}
     * @static
     */
    defineServerProjections,
});

GIS.UI.Map.Static(/** @lends GIS.UI.Map */{
    /**
     * @param {Function|Object|GIS.Map.Base} map
     * @param {Data.ServerSource} source
     * @returns {Promise<Array>}
     * @static
     */
    defineServerProjections,
});


/**
 * @param {Function|Object|GIS.Map.Base} map
 * @param {Data.ServerSource} source
 * @returns {Promise<Array>}
 * todo проверить корректность определения серверных проекций
 */
function defineServerProjections(map, source) {
    return Promise.try(async () => {
        const data = await source.find({
            options: {
                filter: {
                    enabled: true
                }
            }
        });

        map.defineProjection(data.items.map((projection) => {
            const options = projection.extent ? { extent: projection.extent } : null;

            return {
                code: projection.code,
                definition: projection.proj4,
                options
            };
        }));

        return data.items;
    });
}
